
import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { Avatar, Notify, Button, ButtonType, SearchableSelect } from 'components/core';
import { AdminScopeWarning } from 'molecules/userEditor/AdminScopeWarning';
import { useAdminConfirmation } from 'molecules/userEditor/useAdminConfirmation';
import styles from './ConfigLayoutUsers.module.css';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { authState, currentUserState, newItemState, Role, User, usersState, rolesState } from 'states';
import { useUserService, useRolesService, useDashboardService } from 'services';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import {
  initialGenerator,
  getFormErrorMessages,
  isSuperAdminRoleCode,
  getEffectiveStateNames,
  findStateOwningDistricts,
  geoLevelFromRoleCode,
  GeoLevelName,
  isScopeTypeSavedForLevel,
  GeoScopeType,
} from 'helpers';

const isAdminRoleCode = (r?: Role) => r?.admin === true || r?.superAdmin === true;
const isDashboardRoleCode = (r?: Role) => r?.dashboardAdmin === true && !isAdminRoleCode(r);

type TabKey = 'basic' | 'geo' | 'dashboards';
type ScopeCategory = 'admin' | 'dashboard' | null;
type GeoLevel = GeoLevelName | null;
interface ScopeOption {
  key: string;
  label: string;
  desc: string;
  level?: GeoLevel;
}

interface GeoItem {
  id: number;
  name: string;
  type: string;
  parentId?: number;
}

// The `/user/master/list` API's `type` query param values - a shared const so
// every fetchGeoData call site is checked against the same set at compile
// time (a bare string literal here, e.g. a typo'd 'Institute', would silently
// fetch nothing instead of failing to compile).
const GEO_DATA_TYPE = {
  STATE: 'STATE',
  DISTRICT: 'DISTRICT',
  BLOCK: 'BLOCK',
  INSTITUTE: 'INSTITUTE',
} as const;
type GeoDataType = (typeof GEO_DATA_TYPE)[keyof typeof GEO_DATA_TYPE];

// REACT_APP_BASE_API_URL is already the full API base (includes /api/v2 in
// dev/prod, since that's routed via nginx in front of the gateway; locally
// against a bare gateway it has no such prefix) - use it directly rather than
// re-adding a hardcoded /api/v2, which only worked by coincidence when the
// base URL happened to already end in /api/v2.
const GEO_BASE_URL = (process.env.REACT_APP_BASE_API_URL || '').replace(/\/$/, '');

// Not a React component/hook, so callers (inside ConfigLayoutUsers) pass
// their own `t` from useTranslation() rather than this calling the hook itself.
const fetchGeoData = async (type: GeoDataType, t: TFunction, parentId?: number): Promise<GeoItem[]> => {
  try {
    const token = JSON.parse(localStorage.getItem('user') || '{}').authToken;
    const url = parentId
      ? `${GEO_BASE_URL}/user/master/list?type=${type}&parentId=${parentId}`
      : `${GEO_BASE_URL}/user/master/list?type=${type}`;
    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) throw new Error(`${res.status}`);
    const data = await res.json();
     const items = data?.data || data?.responseData || data;
    return Array.isArray(items) ? items : [];
  } catch {
    // Every call site treats [] as "nothing to show" identically to a real
    // empty result, so a failure here would otherwise render as a blank
    // list with no indication anything went wrong - surface it instead.
    Notify.error(t('USER.GEO_LOAD_ERROR', { type: type.toLowerCase() }));
    return [];
  }
};

interface MultiSelectProps {
  options: string[];
  selected: string[];
  onChange: (next: string[]) => void;
  singleSelect?: boolean;
}

const MultiSelectGroup = ({ options, selected, onChange, singleSelect }: MultiSelectProps) => {
  const allSelected = !singleSelect && options.length > 0 && selected.length === options.length;
  const toggleAll = () => {
    if (singleSelect) {
      return;
    }
    onChange(allSelected ? [] : [...options]);
  };
  const toggleOne = (opt: string) => {
    if (singleSelect) { onChange([opt]); return; }
    const next = selected.includes(opt) ? selected.filter((s) => s !== opt) : [...selected, opt];
    onChange(next);
  };
  return (
    <div className={styles.radioGroup}>
      {!singleSelect && (
        <div className={`${styles.radioOpt} ${allSelected ? styles.radioOptSelected : ''}`} onClick={toggleAll}>
          <span className={`${styles.radioDot} ${allSelected ? styles.radioDotSelected : ''}`} />
          All
        </div>
      )}
      {options.map((opt) => {
        const sel = selected.includes(opt);
        return (
          <div key={opt} className={`${styles.radioOpt} ${sel ? styles.radioOptSelected : ''}`} onClick={() => toggleOne(opt)}>
            <span className={`${styles.radioDot} ${sel ? styles.radioDotSelected : ''}`} />
            {opt}
          </div>
        );
      })}
    </div>
  );
};

interface IFormValues {
  firstName: string;
  lastName: string;
  emailId: string;
  phoneNo: string;
}

export const ConfigLayoutUsers = () => {
  const { t } = useTranslation();
  const [currentUser, setCurrentUser] = useRecoilState(currentUserState);
  const [users, setUsers] = useRecoilState(usersState);
  const setIsNewItem = useSetRecoilState(newItemState);
  const userService = useUserService();
  const roleService = useRolesService();
  const dashboardsService = useDashboardService();
  const [roles, setRoles] = useRecoilState(rolesState);
  const loggedInAuth = useRecoilValue(authState);
  const isLoggedInUserSuperAdmin = isSuperAdminRoleCode(loggedInAuth?.roles?.[0]);

  const [activeTab, setActiveTab] = useState<TabKey>('basic');
  const [dashboards, setDashboards] = useState<any[]>([]);

  const ADMIN_SCOPES: ScopeOption[] = roles
    .filter((r: any) => isAdminRoleCode(r) && (isLoggedInUserSuperAdmin || !isSuperAdminRoleCode(r)))
    .map((r: any) => ({ key: r.code, label: r.name, desc: r.description || `${r.name} access` }));

  const DASHBOARD_SCOPES: ScopeOption[] = roles
    .filter((r: any) => isDashboardRoleCode(r))
    .map((r: any) => ({
      key: r.code, label: r.name,
      desc: r.description || `${r.name} access`,
      level: geoLevelFromRoleCode(r.code),
    }));

  const [scopeCategory, setScopeCategory] = useState<ScopeCategory>(null);
  const [selectedScopeKey, setSelectedScopeKey] = useState<string>('');
  const [scopeError, setScopeError] = useState<string>('');

  const [stateItems, setStateItems] = useState<GeoItem[]>([]);
  const [districtItems, setDistrictItems] = useState<GeoItem[]>([]);
  const [blockItems, setBlockItems] = useState<GeoItem[]>([]);
  const [instituteItems, setInstituteItems] = useState<GeoItem[]>([]);
  const [selectedStateIds, setSelectedStateIds] = useState<string[]>([]);
  const [selectedDistrictIds, setSelectedDistrictIds] = useState<string[]>([]);
  const [selectedBlockIds, setSelectedBlockIds] = useState<string[]>([]);
  const [selectedInstituteIds, setSelectedInstituteIds] = useState<string[]>([]);
  const [instituteDistrictFilter, setInstituteDistrictFilter] = useState<string>('');

  const pendingDistricts = useRef<string[]>([]);
  const pendingBlocks = useRef<string[]>([]);
  const pendingInstitutes = useRef<string[]>([]);

  const [pendingScopeVersion, setPendingScopeVersion] = useState(0);
  // District names/ids (and institute values) from a restored scope that has no
  // STATE row of its own - the owning state is looked up once states load.
  const [pendingStateLookup, setPendingStateLookup] = useState<{ districtKeys: string[]; instituteValues: string[] } | null>(null);

  const effectiveStateNames = useMemo(
    () => getEffectiveStateNames(stateItems, selectedStateIds),
    [stateItems, selectedStateIds]
  );
  const primaryStateId = stateItems.find((i) => i.name === effectiveStateNames[0])?.id;

  const stateOptions = stateItems.map((i) => i.name);
  const districtOptions = districtItems.map((i) => i.name);
  const blockOptions = blockItems.map((i) => i.name);

  // Institute names are NOT unique across districts (generic names like "ANM
  // School" repeat) - a bare `.find(i => i.name === x)` would silently resolve
  // to whichever one comes first in the array. Disambiguate every option with
  // its district so the label - and everything keyed off it below (auto-
  // derived district, saved institute id) - is unique per real institute.
  const instituteLabel = (i: GeoItem) => {
    const district = districtItems.find((d) => d.id === i.parentId);
    return district ? `${i.name} (${district.name})` : i.name;
  };
  // With several states, only offer institutes under the selected state's
  // districts (institutes are fetched for every state up front). A single-state
  // deployment keeps the full list, including ones with an unknown district.
  const hasMultipleStates = stateItems.length > 1;
  const stateInstituteItems = hasMultipleStates
    ? instituteItems.filter((i) => districtItems.some((d) => d.id === i.parentId))
    : instituteItems;
  const instituteOptions = stateInstituteItems.map(instituteLabel);

  // The district filter here only narrows the visible institute list for
  // convenience - it is never itself saved as a scope (see onSubmit); the
  // actual district shown/saved is always auto-derived from the institute.
  const filteredInstituteOptions = stateInstituteItems
    .filter((i) => {
      if (!instituteDistrictFilter) return true;
      return districtItems.find((d) => d.id === i.parentId)?.name === instituteDistrictFilter;
    })
    .map(instituteLabel);

  // Resolves a saved institute scope value back to this institute's disambiguated
  // display label. Scopes saved after the collision fix are "InstituteName|
  // DistrictName" (see onSubmit) - unambiguous, since name+district is unique.
  // A value with no "|" is legacy data saved before that fix shipped, resolved
  // by name alone (same best-effort behavior the UI always had for those).
  const resolvePendingInstitute = (value: string, items: GeoItem[]): string => {
    const sep = value.indexOf('|');
    if (sep < 0) {
      const institute = items.find((i) => i.name === value);
      return institute ? instituteLabel(institute) : value;
    }
    const name = value.slice(0, sep);
    const district = value.slice(sep + 1);
    const institute = items.find(
      (i) => i.name === name && districtItems.find((d) => d.id === i.parentId)?.name === district
    );
    return institute ? instituteLabel(institute) : name;
  };

  const selectedDashboardScope = DASHBOARD_SCOPES.find((s) => s.key === selectedScopeKey);
  const adminConfirm = useAdminConfirmation(scopeCategory, selectedScopeKey);
  const geoLevel: GeoLevel = geoLevelFromRoleCode(selectedScopeKey);

  useEffect(() => {
    fetchGeoData(GEO_DATA_TYPE.STATE, t).then(setStateItems);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Districts always come from the selected state's own id, so any number of
  // states works; with a single state it is loaded as soon as states arrive.
  useEffect(() => {
    if (!primaryStateId) return;
    fetchGeoData(GEO_DATA_TYPE.DISTRICT, t, primaryStateId).then((items) => {
      setDistrictItems(items);
      setBlockItems([]);
      if (pendingDistricts.current.length > 0) {
        setSelectedDistrictIds(pendingDistricts.current);
        pendingDistricts.current = [];
      }
    });
  }, [primaryStateId, effectiveStateNames, t]);

  useEffect(() => {
    if (selectedDistrictIds.length === 0) { setBlockItems([]); return; }
    const districtId = districtItems.find((d) => d.name === selectedDistrictIds[0])?.id;
    if (!districtId) return;
    fetchGeoData(GEO_DATA_TYPE.BLOCK, t, districtId).then((items) => {
      setBlockItems(items);
      if (pendingBlocks.current.length > 0) {
        setSelectedBlockIds(pendingBlocks.current);
        pendingBlocks.current = [];
      }
    });
  }, [selectedDistrictIds, districtItems, t]);

  // Institute search must cover every institute in the state up front, not just
  // the ones under a manually-picked district - the whole point of the search
  // box is to let the admin find an institute directly, with its district
  // inferred afterward from the institute's own parentId, rather than forcing
  // them to already know/pick the district before they can even search.
  useEffect(() => {
    if (geoLevel !== 'institute') { setInstituteItems([]); return; }
    fetchGeoData(GEO_DATA_TYPE.INSTITUTE, t).then((items) => setInstituteItems(items));
  }, [geoLevel, t]);

  // Resolving a pending institute selection needs BOTH lists ready:
  // instituteItems (to find the record) AND districtItems (instituteLabel()
  // uses it to build the disambiguated "Name (District)" label). Institute
  // is a single fetch while districts arrive via a slower two-hop STATE ->
  // DISTRICT chain, so resolving as soon as instituteItems alone loaded would
  // produce a bare, undisambiguated label - one that no longer matches
  // instituteOptions once districtItems arrives and options get recomputed
  // with the district suffix, leaving the saved institute looking unselected.
  useEffect(() => {
    if (pendingInstitutes.current.length === 0) return;
    if (instituteItems.length === 0 || districtItems.length === 0) return;
    setSelectedInstituteIds(pendingInstitutes.current.map((v) => resolvePendingInstitute(v, instituteItems)));
    pendingInstitutes.current = [];
  }, [instituteItems, districtItems, pendingScopeVersion]);

  // Once an institute is selected (interactively, or restored from a saved
  // scope above), auto-derive its district from the institute's own parentId
  // and set it purely for display - it is never saved as its own DISTRICT
  // scope for institute-level roles (see the onSubmit comment below).
  useEffect(() => {
    if (geoLevel !== 'institute' || selectedInstituteIds.length === 0) return;
    const institute = instituteItems.find((i) => instituteLabel(i) === selectedInstituteIds[0]);
    const district = institute && districtItems.find((d) => d.id === institute.parentId);
    if (district) setSelectedDistrictIds([district.name]);
  }, [selectedInstituteIds, instituteItems, districtItems, geoLevel]);

  const handleStateChange = useCallback((val: string[]) => {
    setSelectedStateIds(val);
    setSelectedDistrictIds([]);
    setSelectedBlockIds([]);
    setSelectedInstituteIds([]);
    setInstituteDistrictFilter('');
  }, []);

  useEffect(() => { if (districtItems.length === 1 && selectedDistrictIds.length === 0) setSelectedDistrictIds([districtItems[0].name]); }, [districtItems]);
  useEffect(() => { if (blockItems.length === 1 && selectedBlockIds.length === 0) setSelectedBlockIds([blockItems[0].name]); }, [blockItems]);

  // A restored scope with districts/institutes but no STATE row: find the state
  // that owns them instead of assuming one. Only needed with several states.
  useEffect(() => {
    if (!pendingStateLookup || stateItems.length === 0) return;
    if (stateItems.length === 1) {
      // Nothing to look up - just reload the lone state's districts so the
      // restored (pending) districts get applied.
      setPendingStateLookup(null);
      setSelectedStateIds([stateItems[0].name]);
      return;
    }
    const { districtKeys, instituteValues } = pendingStateLookup;
    const instituteDistrictIds = instituteItems
      .filter((i) => instituteValues.some((v) => v.split('|')[0] === i.name))
      .map((i) => String(i.parentId));
    if (districtKeys.length === 0 && instituteDistrictIds.length === 0) return; // wait for institutes
    setPendingStateLookup(null);
    findStateOwningDistricts(stateItems, [...districtKeys, ...instituteDistrictIds], (stateId) =>
      fetchGeoData(GEO_DATA_TYPE.DISTRICT, t, stateId)
    ).then((state) => { if (state) setSelectedStateIds([state.name]); });
  }, [pendingStateLookup, stateItems, instituteItems, t]);

  const handleDistrictChange = useCallback((val: string[]) => {
    setSelectedDistrictIds(val);
    setSelectedBlockIds([]);
    setSelectedInstituteIds([]);
  }, []);

  const handleInstituteChange = useCallback((name: string) => setSelectedInstituteIds([name]), []);

  const renderInstituteEmpty = useCallback(
    (searchTerm: string) => (
      <>
        <span className="material-icons" style={{ fontSize: 14 }}>info</span>
        {searchTerm
          ? <>No institutes match &quot;{searchTerm}&quot;{instituteDistrictFilter ? ` in ${instituteDistrictFilter}` : ''}.</>
          : <>No institutes found in {instituteDistrictFilter}.</>}
      </>
    ),
    [instituteDistrictFilter],
  );

  const validationSchema = Yup.object().shape({
    firstName: Yup.string().required(t('VALIDATION.FIRST_NAME_REQUIRED')),
    emailId: Yup.string()
      .required(t('VALIDATION.EMAIL_REQUIRED'))
      .email(t('VALIDATION.EMAIL_INVALID'))
      .test('email-unique', t('VALIDATION.EMAIL_DUPLICATE'), (val) => {
        if (!val) return true;
        return !users.some((u: User) => u.emailId === val && u.id !== currentUser.id);
      }),
    phoneNo: Yup.string()
      .required(t('VALIDATION.PHONE_REQUIRED'))
      .matches(/^\d{10}$/, t('VALIDATION.PHONE_INVALID'))
      .test('phone-unique', t('VALIDATION.PHONE_DUPLICATE'), (val) => {
        if (!val) return true;
        return !users.some((u: User) => u.phoneNo === val && u.id !== currentUser.id);
      }),
  });

  const { register, handleSubmit, formState, reset, setValue, getValues, trigger } =
    useForm<IFormValues>({
      resolver: yupResolver(validationSchema),
      context: { isNewUser: !currentUser.id },
      defaultValues: {
        firstName: currentUser.id ? currentUser.firstName || '' : '',
        lastName:  currentUser.id ? (currentUser as any).lastName || '' : '',
        emailId:   currentUser.id ? currentUser.emailId || '' : '',
        phoneNo:   currentUser.id ? currentUser.phoneNo || '' : '',
      },
    });

  const { errors, isSubmitting } = formState;

  useEffect(() => { roleService.getRoles().then((r: any) => { if (r.length > 0) setRoles(r); }); }, []);

  // Institute-level roles only ever persist an INSTITUTE scope row (the
  // district picker is just a navigation aid, never saved as its own grant -
  // see the onSubmit comment below), so there is no DISTRICT scope to read
  // back here. The district shown for an existing InstituteAdmin is instead
  // re-derived automatically from the institute's own parentId once it's
  // restored below (see the auto-derive effect above) - no extra fetch needed.
  const restorePendingDashboardScopes = (userId: number) => {
    const token = JSON.parse(localStorage.getItem('user') || '{}').authToken;
    fetch(`${GEO_BASE_URL}/user/geo-scope/list/${userId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!data) return;
        const scopes: any[] = data?.data || data?.responseData || data || [];
        const stateNames     = scopes.filter((s: any) => s.scopeType === 'STATE').map((s: any) => s.scopeName || s.scopeValue);
        const districtNames  = scopes.filter((s: any) => s.scopeType === 'DISTRICT').map((s: any) => s.scopeName || s.scopeValue);
        const blockNames     = scopes.filter((s: any) => s.scopeType === 'BLOCK').map((s: any) => s.scopeName || s.scopeValue);
        const instituteNames = scopes.filter((s: any) => s.scopeType === 'INSTITUTE').map((s: any) => s.scopeName || s.scopeValue);

        if (districtNames.length > 0) pendingDistricts.current = districtNames;
        if (blockNames.length > 0) pendingBlocks.current = blockNames;
        if (instituteNames.length > 0) pendingInstitutes.current = instituteNames;
        // Institute-level roles have no DISTRICT scope of their own to
        // trigger setSelectedStateIds above, so the institute-resolving
        // effect (keyed on pendingScopeVersion) needs its own explicit
        // nudge once pendingInstitutes is actually populated.
        if (instituteNames.length > 0) setPendingScopeVersion((v) => v + 1);
        if (stateNames.length > 0) {
          setSelectedStateIds(stateNames);
        } else if (districtNames.length > 0 || instituteNames.length > 0) {
          const instituteDistricts = instituteNames.filter((v) => v.includes('|')).map((v) => v.slice(v.indexOf('|') + 1));
          setPendingStateLookup({ districtKeys: [...districtNames, ...instituteDistricts], instituteValues: instituteNames });
        }
      })
      // eslint-disable-next-line @typescript-eslint/no-empty-function -- intentional: non-critical geo-lookup, safe to ignore failures
      .catch(() => {});
  };

  useEffect(() => {
    reset();
    setValue('firstName', currentUser.id ? currentUser.firstName || '' : '');
    setValue('lastName',  currentUser.id ? (currentUser as any).lastName || '' : '');
    setValue('emailId',   currentUser.id ? currentUser.emailId || '' : '');
    setValue('phoneNo',   currentUser.id ? currentUser.phoneNo || '' : '');

    const roleObj: Role = currentUser.roles?.[0] || {};
    const roleCode = roleObj.code || currentUser.scopeLevel || '';

    let resolvedCategory: ScopeCategory = null;
    if (isAdminRoleCode(roleObj)) {
      resolvedCategory = 'admin';
    } else if (isDashboardRoleCode(roleObj)) {
      resolvedCategory = 'dashboard';
    }

    setScopeCategory(resolvedCategory);
    setSelectedScopeKey(roleCode);
    adminConfirm.setConfirmedRole(currentUser.id && resolvedCategory === 'admin' ? roleCode : '');
    setSelectedStateIds([]);
    setSelectedDistrictIds([]);
    setSelectedBlockIds([]);
    setSelectedInstituteIds([]);
    setInstituteDistrictFilter('');
    pendingDistricts.current = [];
    pendingBlocks.current = [];
    pendingInstitutes.current = [];
    setPendingStateLookup(null);

    if (currentUser.id && resolvedCategory === 'dashboard') {
      restorePendingDashboardScopes(currentUser.id);
    }

    setActiveTab('basic');
    setScopeError('');
  }, [currentUser]);

  useEffect(() => {
    if (!selectedScopeKey) { setDashboards([]); return; }
    const matchedRole = roles.find((r: any) => r.code === selectedScopeKey);
    const roleId = (matchedRole as any)?.id;
    if (!roleId) { setDashboards([]); return; }
    dashboardsService
      .getDashboardsForRole(roleId)
      .then((res: any) => setDashboards(Array.isArray(res) ? res : res?.responseData || []))
      .catch(() => setDashboards([]));
  }, [selectedScopeKey, roles]);

  const handleBack = () => {
    const unsavedIdx = users.findIndex((u: User) => u.id === undefined);
    if (unsavedIdx !== -1) { const updated = [...users]; updated.splice(unsavedIdx, 1); setUsers(updated); setIsNewItem(false); }
    setCurrentUser({} as User);
    reset();
  };

  const onInvalid = (formErrors: typeof formState.errors) => {
    Notify.error(getFormErrorMessages(formErrors) || t('VALIDATION.ERROR_GENERIC'));
  };

  const handleScopeCardClick = (category: ScopeCategory, key: string) => {
    setScopeCategory(category);
    setSelectedScopeKey(key);
    setScopeError('');
    setSelectedStateIds([]);
    setSelectedDistrictIds([]);
    setSelectedBlockIds([]);
    setSelectedInstituteIds([]);
    setInstituteDistrictFilter('');
    pendingDistricts.current = [];
    pendingBlocks.current = [];
    pendingInstitutes.current = [];
  };

  // Only the levels the role actually uses are saved (SCOPE_TYPES_BY_GEO_LEVEL):
  // lower levels may hold an auto-selected value purely for display - e.g. a
  // state's only district, a district's only block, or an institute's derived
  // district - and saving those would silently narrow (or, for an institute's
  // district, widen) what the user can see.
  const buildScopePayload = (roleCode: string) => {
    const scopePayload: { scopeType: string; scopeValue: string[]; roleCode: string }[] = [];
    const saves = (scopeType: GeoScopeType, names: string[]) => isScopeTypeSavedForLevel(geoLevel, scopeType) && names.length > 0;
    if (saves('STATE', effectiveStateNames)) {
      scopePayload.push({ scopeType: 'STATE', scopeValue: effectiveStateNames.map((n) => String(stateItems.find((i) => i.name === n)?.id || n)), roleCode });
    }
    if (saves('DISTRICT', selectedDistrictIds)) {
      scopePayload.push({ scopeType: 'DISTRICT', scopeValue: selectedDistrictIds.map((n) => String(districtItems.find((i) => i.name === n)?.id || n)), roleCode });
    }
    if (saves('BLOCK', selectedBlockIds)) {
      scopePayload.push({ scopeType: 'BLOCK', scopeValue: selectedBlockIds.map((n) => String(blockItems.find((i) => i.name === n)?.id || n)), roleCode });
    }
    if (saves('INSTITUTE', selectedInstituteIds)) {
      scopePayload.push({ scopeType: 'INSTITUTE', scopeValue: selectedInstituteIds.map((n) => String(instituteItems.find((i) => instituteLabel(i) === n)?.id || n)), roleCode });
    }
    return scopePayload;
  };

  const saveGeoScope = (userId: number, scopePayload: ReturnType<typeof buildScopePayload>) => {
    if (scopePayload.length === 0) return;
    const token = JSON.parse(localStorage.getItem('user') || '{}').authToken;
    fetch(`${GEO_BASE_URL}/user/geo-scope/replace?userId=${userId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(scopePayload),
    }).catch((e) => Notify.error(`Geo scope assignment failed: ${e}`));
  };

  const onSubmit = (values: IFormValues) => {
    if (!selectedScopeKey) { setScopeError(t('USER.SCOPE_ERROR_SELECT')); return; }
    if (adminConfirm.needsConfirmation) { setScopeError(t('USER.ADMIN_CONFIRM_ERROR')); setActiveTab('basic'); return; }

    const matchedRole =
      roles.find((r: any) => r.code === selectedScopeKey) ||
      roles.find((r: any) => isAdminRoleCode(r) || isDashboardRoleCode(r));

    const updatedUserDetail = {
      ...currentUser,
      firstName: values.firstName,
      lastName:  values.lastName || null,
      emailId:   values.emailId,
      phoneNo:   values.phoneNo,
      username:  values.emailId,
      roles: matchedRole ? [matchedRole] : (currentUser.roles || []).filter(Boolean),
      scopeLevel: selectedScopeKey,
    };

    userService.createOrUpdateUser(updatedUserDetail).then(
      (response: any) => {
        const userId =
          (Array.isArray(response) ? response[0]?.id : null) ||
          response?.responseData?.[0]?.id ||
          response?.id ||
          currentUser.id;

        if (scopeCategory === 'dashboard' && userId) {
          const roleCode = matchedRole?.code || selectedScopeKey;
          saveGeoScope(userId, buildScopePayload(roleCode));
        }

        Notify.success(currentUser.id === undefined ? t('USER.SAVE_SUCCESS') : t('USER.UPDATE_SUCCESS'));
        setIsNewItem(false);
        userService.getUsers(true).then((u) => setUsers(u));
        setCurrentUser({} as User);
        reset();
      },
      (error) => Notify.error(error?.message || error?.statusInfo?.errorMessage || error || t('VALIDATION.ERROR_GENERIC'))
    );
  };

  let tabIdx = 2;
  if (activeTab === 'basic') {
    tabIdx = 0;
  } else if (activeTab === 'geo') {
    tabIdx = 1;
  }
  const isSingleState = stateItems.length === 1;

  // Basic step is only valid once its required fields pass validation and a scope is chosen.
  const isBasicStepValid =
    !errors.firstName && !errors.emailId && !errors.phoneNo &&
    Boolean(getValues('firstName')) && Boolean(getValues('emailId')) && Boolean(getValues('phoneNo')) &&
    Boolean(selectedScopeKey);

  // Geo step only applies to the dashboard scope category — admin scope has no geo requirement.
  const isGeoStepValid = (() => {
    if (scopeCategory !== 'dashboard') return true;
    if (geoLevel === 'state') return true;
    if (geoLevel === 'district') return selectedDistrictIds.length > 0;
    if (geoLevel === 'block') return selectedBlockIds.length > 0;
    if (geoLevel === 'facility') return selectedBlockIds.length > 0;
    if (geoLevel === 'institute') return selectedInstituteIds.length > 0;
    return true;
  })();

  const canAccessGeoTab = isBasicStepValid;
  const canAccessDashboardsTab = scopeCategory === 'admin' ? isBasicStepValid : isBasicStepValid && isGeoStepValid;

  const canAccessTab = (tab: TabKey) => {
    if (tab === 'basic') return true;
    if (tab === 'geo') return canAccessGeoTab;
    return canAccessDashboardsTab;
  };

  // Single gate for every forward-navigation attempt (Next buttons, tab clicks, stepper dots) —
  // re-validates the step(s) being skipped past and toasts exactly what's missing/invalid.
  const goToTab = async (tab: TabKey) => {
    if (tab === 'basic') { setActiveTab('basic'); return; }

    const basicValid = await trigger(['firstName', 'emailId', 'phoneNo']);
    if (!basicValid) {
      Notify.error(getFormErrorMessages(formState.errors) || t('VALIDATION.ERROR_GENERIC'));
      return;
    }
    if (!selectedScopeKey) {
      setScopeError(t('USER.SCOPE_ERROR_SELECT'));
      Notify.error(t('USER.SCOPE_ERROR_SELECT'));
      return;
    }
    if (adminConfirm.needsConfirmation) {
      setScopeError(t('USER.ADMIN_CONFIRM_ERROR'));
      Notify.error(t('USER.ADMIN_CONFIRM_ERROR'));
      return;
    }
    setScopeError('');

    if (tab === 'dashboards' && scopeCategory !== 'admin' && !isGeoStepValid) {
      Notify.error(t('USER.GEO_ERROR_SELECT'));
      return;
    }

    setActiveTab(tab);
  };

  const renderProfileFields = () => (
    <div className={styles.formGrid}>
      <div className={styles.formGroup}>
        <div className={styles.formLabel}>{t('USER.FIELD_FIRST_NAME')} <span className={styles.required}>*</span></div>
        <input
          className={`${styles.formInput} ${errors.firstName ? styles.inputError : ''}`}
          placeholder={t('USER.PLACEHOLDER_FIRST_NAME')}
          {...register('firstName')}
          onChange={(e) => setValue('firstName', e.target.value, { shouldValidate: true })}
        />
        {errors.firstName && <div className={styles.errorMsg}>{errors.firstName.message}</div>}
      </div>
      <div className={styles.formGroup}>
        <div className={styles.formLabel}>{t('USER.FIELD_LAST_NAME')}</div>
        <input
          className={styles.formInput}
          placeholder={t('USER.PLACEHOLDER_LAST_NAME')}
          defaultValue={(currentUser as any).lastName || ''}
          onChange={(e) => setValue('lastName', e.target.value, { shouldValidate: false })}
        />
      </div>
      <div className={styles.formGroup}>
        <div className={styles.formLabel}>{t('USER.FIELD_EMAIL')} <span className={styles.required}>*</span></div>
        <input
          className={`${styles.formInput} ${errors.emailId ? styles.inputError : ''} ${currentUser.id ? styles.inputReadOnly : ''}`}
          placeholder={t('USER.PLACEHOLDER_EMAIL')}
          type="email"
          {...register('emailId')}
          onChange={(e) => setValue('emailId', e.target.value, { shouldValidate: true })}
          readOnly={Boolean(currentUser.id)}
        />
        {currentUser.id && <div className={styles.fieldHint}>{t('USER.EMAIL_READONLY_HINT')}</div>}
        {errors.emailId && <div className={styles.errorMsg}>{errors.emailId.message}</div>}
      </div>
      <div className={styles.formGroup}>
        <div className={styles.formLabel}>{t('USER.FIELD_PHONE')} <span className={styles.required}>*</span></div>
        <input
          className={`${styles.formInput} ${errors.phoneNo ? styles.inputError : ''}`}
          placeholder={t('USER.PLACEHOLDER_PHONE')}
          {...register('phoneNo')}
          onChange={(e) => setValue('phoneNo', e.target.value, { shouldValidate: true })}
        />
        {errors.phoneNo && <div className={styles.errorMsg}>{errors.phoneNo.message}</div>}
      </div>
    </div>
  );

  const renderScopeRoleGrid = () => (
    <div className={styles.scopeRoleGrid}>
      {(scopeCategory === 'admin' ? ADMIN_SCOPES : DASHBOARD_SCOPES).map((scope) => {
        const isSel = selectedScopeKey === scope.key;
        let scopeCardSelClass = '';
        let scopeCheckSelClass = '';
        if (isSel) {
          scopeCardSelClass = scopeCategory === 'admin' ? styles.scopeRoleCardSelAdmin : styles.scopeRoleCardSelDash;
          scopeCheckSelClass = scopeCategory === 'admin' ? styles.scopeRoleCheckAdmin : styles.scopeRoleCheckDash;
        }
        return (
          <div
            key={scope.key}
            className={`${styles.scopeRoleCard} ${scopeCardSelClass}`}
            onClick={() => handleScopeCardClick(scopeCategory, scope.key)}
          >
            <div className={styles.scopeRoleCardTop}>
              <span className={`${styles.scopeRoleCheck} ${scopeCheckSelClass}`}>
                {isSel && <span className="material-icons" style={{ fontSize: 10, color: 'var(--text-on-primary)' }}>check</span>}
              </span>
              <span className={styles.scopeRoleName}>{scope.label}</span>
            </div>
            <span className={styles.scopeRoleDesc}>{scope.desc}</span>
          </div>
        );
      })}
    </div>
  );

  const renderScopeSection = () => (
    <div className={styles.scopeSection}>
      <div className={styles.scopeSectionTitle}>{t('USER.SCOPE_SECTION_TITLE')} <span className={styles.required}>*</span></div>
      <div className={styles.scopeNote}>{t('USER.SCOPE_SECTION_NOTE')}</div>
      <div className={styles.scopeRadioRow}>
        <label
          className={`${styles.scopeRadioLabel} ${scopeCategory === 'admin' ? styles.scopeRadioLabelActive : ''}`}
          onClick={() => { setScopeCategory('admin'); setSelectedScopeKey(''); setScopeError(''); }}
        >
          <span className={`${styles.scopeRadioCircle} ${scopeCategory === 'admin' ? styles.scopeRadioCircleActive : ''}`}>
            {scopeCategory === 'admin' && <span className={styles.scopeRadioInner} />}
          </span>
          <div>
            <div className={styles.scopeRadioName}>{t('USER.SCOPE_ADMIN_NAME')}</div>
            <div className={styles.scopeRadioDesc}>{t('USER.SCOPE_ADMIN_DESC')}</div>
          </div>
        </label>
        <label
          className={`${styles.scopeRadioLabel} ${scopeCategory === 'dashboard' ? styles.scopeRadioLabelActiveDash : ''}`}
          onClick={() => { setScopeCategory('dashboard'); setSelectedScopeKey(''); setScopeError(''); }}
        >
          <span className={`${styles.scopeRadioCircle} ${scopeCategory === 'dashboard' ? styles.scopeRadioCircleActiveDash : ''}`}>
            {scopeCategory === 'dashboard' && <span className={`${styles.scopeRadioInner} ${styles.scopeRadioInnerDash}`} />}
          </span>
          <div>
            <div className={styles.scopeRadioName}>{t('USER.SCOPE_DASHBOARD_NAME')}</div>
            <div className={styles.scopeRadioDesc}>{t('USER.SCOPE_DASHBOARD_DESC')}</div>
          </div>
        </label>
      </div>

      {scopeCategory && renderScopeRoleGrid()}
      {scopeCategory === 'admin' && selectedScopeKey && (
        <AdminScopeWarning
          roleLabel={ADMIN_SCOPES.find((r) => r.key === selectedScopeKey)?.label ?? ''}
          confirmed={adminConfirm.confirmed}
          onToggle={() => { adminConfirm.toggle(); setScopeError(''); }}
        />
      )}
      {scopeError && (
        <div className={styles.scopeError}>
          <span className="material-icons" style={{ fontSize: 14 }}>error_outline</span>
          {scopeError}
        </div>
      )}
    </div>
  );

  const renderBasicTab = () => (
    <div>
      {renderProfileFields()}
      {renderScopeSection()}
      <div className={styles.footer}>
        <button type="button" className={styles.btnCancel} onClick={handleBack}>
          <span className="material-icons" style={{ fontSize: 15 }}>close</span>
          {t('ACTION.CANCEL')}
        </button>
        <button
          type="button"
          className={`${styles.btnNext} ${!isBasicStepValid ? styles.tabDisabled : ''}`}
          onClick={() => goToTab(scopeCategory === 'admin' ? 'dashboards' : 'geo')}
        >
          {scopeCategory === 'admin' ? t('USER.BTN_NEXT_DASHBOARDS') : t('USER.BTN_NEXT_GEO')}
          <span className="material-icons" style={{ fontSize: 15 }}>arrow_forward</span>
        </button>
      </div>
    </div>
  );

  const renderGeoStateSection = () => (
    <div className={styles.geoSection}>
      {isSingleState ? (
        <>
          <div className={styles.infoNote} style={{ marginBottom: 12 }}>
            <span className="material-icons" style={{ fontSize: 14 }}>location_on</span>
            {t('USER.GEO_STATE_SINGLE')} <strong>{stateItems[0]?.name}</strong>
          </div>
          <div className={styles.infoNote}>
            <span className="material-icons" style={{ fontSize: 14 }}>check_circle</span>
            {t('USER.GEO_STATE_AUTO_NOTE')} {stateItems[0]?.name}
          </div>
        </>
      ) : (
        <>
          <div className={styles.geoTitle}>
            <span className="material-icons" style={{ fontSize: 16, color: 'var(--primary-1)' }}>location_on</span>
            {t('USER.GEO_SELECT_STATE')}
            {selectedStateIds.length > 0 && <span className={styles.selCount}>{selectedStateIds.length} {t('USER.GEO_SELECTED_COUNT')}</span>}
          </div>
          <MultiSelectGroup options={stateOptions} selected={selectedStateIds} onChange={handleStateChange} />
          {selectedStateIds.length > 0 && (
            <div className={styles.infoNote} style={{ marginTop: 10 }}>
              <span className="material-icons" style={{ fontSize: 14 }}>check_circle</span>
              {t('USER.GEO_STATE_AUTO_NOTE')} {selectedStateIds.join(', ')}
            </div>
          )}
        </>
      )}
    </div>
  );

  const renderGeoDistrictSection = () => (
    <>
      {!isSingleState && (
        <div className={styles.geoSection}>
          <div className={styles.geoTitle}>
            <span className="material-icons" style={{ fontSize: 16, color: 'var(--primary-1)' }}>location_on</span>
            {t('USER.GEO_STEP_STATE')}
          </div>
          <MultiSelectGroup options={stateOptions} selected={selectedStateIds} onChange={handleStateChange} singleSelect />
        </div>
      )}
      {isSingleState && (
        <div className={styles.infoNote} style={{ marginBottom: 12 }}>
          <span className="material-icons" style={{ fontSize: 14 }}>location_on</span>
          {t('USER.GEO_STATE_SINGLE')} <strong>{stateItems[0]?.name}</strong>
        </div>
      )}
      {districtOptions.length > 0 && (
        <div className={styles.geoSection}>
          <div className={styles.geoTitle}>
            <span className="material-icons" style={{ fontSize: 16, color: 'var(--primary-1)' }}>map</span>
            {isSingleState ? t('USER.GEO_SELECT_DISTRICT') : t('USER.GEO_STEP_DISTRICT_MULTI')}
            {selectedDistrictIds.length > 0 && <span className={styles.selCount}>{selectedDistrictIds.length} {t('USER.GEO_SELECTED_COUNT')}</span>}
          </div>
          <MultiSelectGroup options={districtOptions} selected={selectedDistrictIds} onChange={handleDistrictChange} />
          {selectedDistrictIds.length > 0 && (
            <div className={styles.infoNote} style={{ marginTop: 10 }}>
              <span className="material-icons" style={{ fontSize: 14 }}>check_circle</span>
              {t('USER.GEO_DISTRICT_AUTO_NOTE')}
            </div>
          )}
        </div>
      )}
    </>
  );

  const renderGeoBlockSection = () => (
    <>
      {!isSingleState && (
        <div className={styles.geoSection}>
          <div className={styles.geoTitle}>
            <span className="material-icons" style={{ fontSize: 16, color: 'var(--primary-1)' }}>location_on</span>
            {t('USER.GEO_STEP_STATE')}
          </div>
          <MultiSelectGroup options={stateOptions} selected={selectedStateIds} onChange={handleStateChange} singleSelect />
        </div>
      )}
      {isSingleState && (
        <div className={styles.infoNote} style={{ marginBottom: 12 }}>
          <span className="material-icons" style={{ fontSize: 14 }}>location_on</span>
          {t('USER.GEO_STATE_SINGLE')} <strong>{stateItems[0]?.name}</strong>
        </div>
      )}
      {districtOptions.length > 0 && (
        <div className={styles.geoSection}>
          <div className={styles.geoTitle}>
            <span className="material-icons" style={{ fontSize: 16, color: 'var(--primary-1)' }}>map</span>
            {isSingleState ? t('USER.GEO_STEP_DISTRICT_SINGLE') : t('USER.GEO_STEP_DISTRICT_MULTI')}
            {selectedDistrictIds.length > 0 && <span className={styles.selCount}>{selectedDistrictIds.length} {t('USER.GEO_SELECTED_COUNT')}</span>}
          </div>
          <MultiSelectGroup options={districtOptions} selected={selectedDistrictIds} onChange={handleDistrictChange} />
        </div>
      )}
      {blockOptions.length > 0 && (
        <div className={styles.geoSection}>
          <div className={styles.geoTitle}>
            <span className="material-icons" style={{ fontSize: 16, color: 'var(--primary-1)' }}>account_tree</span>
            {isSingleState ? t('USER.GEO_STEP_BLOCK_SINGLE') : t('USER.GEO_STEP_BLOCK_MULTI')}
            {selectedBlockIds.length > 0 && <span className={styles.selCount}>{selectedBlockIds.length} {t('USER.GEO_SELECTED_COUNT')}</span>}
          </div>
          <MultiSelectGroup options={blockOptions} selected={selectedBlockIds} onChange={setSelectedBlockIds} />
        </div>
      )}
    </>
  );

  const renderGeoFacilitySection = () => (
    <>
      {!isSingleState && (
        <div className={styles.geoSection}>
          <div className={styles.geoTitle}>
            <span className="material-icons" style={{ fontSize: 16, color: 'var(--primary-1)' }}>location_on</span>
            {t('USER.GEO_STEP_STATE')}
          </div>
          <MultiSelectGroup options={stateOptions} selected={selectedStateIds} onChange={handleStateChange} singleSelect />
        </div>
      )}
      {isSingleState && (
        <div className={styles.infoNote} style={{ marginBottom: 12 }}>
          <span className="material-icons" style={{ fontSize: 14 }}>location_on</span>
          {t('USER.GEO_STATE_SINGLE')} <strong>{stateItems[0]?.name}</strong>
        </div>
      )}
      {districtOptions.length > 0 && (
        <div className={styles.geoSection}>
          <div className={styles.geoTitle}>
            <span className="material-icons" style={{ fontSize: 16, color: 'var(--primary-1)' }}>map</span>
            {isSingleState ? t('USER.GEO_STEP_DISTRICT_SINGLE') : t('USER.GEO_STEP_DISTRICT_MULTI')}
            {selectedDistrictIds.length > 0 && <span className={styles.selCount}>{selectedDistrictIds.length} {t('USER.GEO_SELECTED_COUNT')}</span>}
          </div>
          <MultiSelectGroup options={districtOptions} selected={selectedDistrictIds} onChange={handleDistrictChange} />
        </div>
      )}
      {blockOptions.length > 0 && (
        <div className={styles.geoSection}>
          <div className={styles.geoTitle}>
            <span className="material-icons" style={{ fontSize: 16, color: 'var(--primary-1)' }}>account_tree</span>
            {isSingleState ? t('USER.GEO_STEP_BLOCK_SINGLE') : t('USER.GEO_STEP_BLOCK_MULTI')}
            {selectedBlockIds.length > 0 && <span className={styles.selCount}>{selectedBlockIds.length} {t('USER.GEO_SELECTED_COUNT')}</span>}
          </div>
          <MultiSelectGroup options={blockOptions} selected={selectedBlockIds} onChange={setSelectedBlockIds} />
        </div>
      )}
      {selectedBlockIds.length > 0 && (
        <div className={styles.geoSection}>
          <div className={styles.geoTitle}>
            <span className="material-icons" style={{ fontSize: 16, color: 'var(--primary-1)' }}>local_hospital</span>
            {isSingleState ? t('USER.GEO_STEP_FACILITY_SINGLE') : t('USER.GEO_STEP_FACILITY_MULTI')}
          </div>
          <div className={styles.infoNote}>
            <span className="material-icons" style={{ fontSize: 14 }}>info</span>
            {t('USER.GEO_FACILITY_AUTO_NOTE')}
          </div>
        </div>
      )}
    </>
  );

  const instituteEmptyMessage = () => {
    if (hasMultipleStates && effectiveStateNames.length === 0) return t('USER.GEO_SELECT_STATE_FIRST');
    if (hasMultipleStates && instituteItems.length > 0 && districtItems.length > 0) return t('USER.GEO_NO_STATE_INSTITUTES');
    return t('USER.GEO_LOADING_INSTITUTES');
  };

  const renderGeoInstituteSection = () => (
    <>
      {!isSingleState && (
        <div className={styles.geoSection}>
          <div className={styles.geoTitle}>
            <span className="material-icons" style={{ fontSize: 16, color: 'var(--primary-1)' }}>location_on</span>
            {t('USER.GEO_STEP_STATE')}
          </div>
          <MultiSelectGroup options={stateOptions} selected={selectedStateIds} onChange={handleStateChange} singleSelect />
        </div>
      )}
      {isSingleState && (
        <div className={styles.infoNote} style={{ marginBottom: 12 }}>
          <span className="material-icons" style={{ fontSize: 14 }}>location_on</span>
          {t('USER.GEO_STATE_SINGLE')} <strong>{stateItems[0]?.name}</strong>
        </div>
      )}
      <div className={styles.geoSection}>
        <div className={styles.geoTitle}>
          <span className="material-icons" style={{ fontSize: 16, color: 'var(--primary-1)' }}>school</span>
          {t('USER.GEO_INSTITUTE_TITLE')}
          {selectedInstituteIds.length > 0 && <span className={styles.selCount}>{selectedInstituteIds.length} {t('USER.GEO_SELECTED_COUNT')}</span>}
        </div>
        {instituteOptions.length > 0 ? (
          <>
            <div className={styles.formGroup} style={{ marginBottom: 10 }}>
              <div className={styles.formLabel}>{t('USER.GEO_FILTER_BY_DISTRICT')}</div>
              <select
                className={styles.formInput}
                value={instituteDistrictFilter}
                onChange={(e) => setInstituteDistrictFilter(e.target.value)}
              >
                <option value="">{t('USER.GEO_ALL_DISTRICTS')}</option>
                {districtOptions.map((name) => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </select>
            </div>
            <SearchableSelect
              options={filteredInstituteOptions}
              selected={selectedInstituteIds[0] || ''}
              onChange={handleInstituteChange}
              placeholder={t('USER.GEO_SELECT_INSTITUTE_PLACEHOLDER')}
              searchPlaceholder={t('USER.GEO_SEARCH_INSTITUTE_PLACEHOLDER')}
              icon="school"
              renderEmpty={renderInstituteEmpty}
            />
          </>
        ) : (
          <div className={styles.infoNote}>
            <span className="material-icons" style={{ fontSize: 14 }}>info</span>
            {instituteEmptyMessage()}
          </div>
        )}
      </div>
      {selectedInstituteIds.length > 0 && selectedDistrictIds.length > 0 && (
        <div className={styles.infoNote} style={{ marginTop: 10 }}>
          <span className="material-icons" style={{ fontSize: 14 }}>check_circle</span>
          {t('USER.GEO_DISTRICT_LABEL')} <strong>{selectedDistrictIds[0]}</strong> {t('USER.GEO_AUTO_ASSIGNED_FROM_INSTITUTE')}
        </div>
      )}
    </>
  );

  const renderGeoTab = () => (
    <div>
      {selectedDashboardScope && (
        <div className={styles.roleHint}>
          <span className="material-icons" style={{ fontSize: 16, flexShrink: 0 }}>info</span>
          <span><strong>{selectedDashboardScope.label}:</strong> {selectedDashboardScope.desc}</span>
        </div>
      )}

      {geoLevel === 'state' && renderGeoStateSection()}
      {geoLevel === 'district' && renderGeoDistrictSection()}
      {geoLevel === 'block' && renderGeoBlockSection()}
      {geoLevel === 'facility' && renderGeoFacilitySection()}
      {geoLevel === 'institute' && renderGeoInstituteSection()}

      <div className={styles.footer}>
        <button type="button" className={styles.btnCancel} onClick={() => setActiveTab('basic')}>
          <span className="material-icons" style={{ fontSize: 15 }}>arrow_back</span>
          {t('ACTION.BACK')}
        </button>
        <button
          type="button"
          className={`${styles.btnNext} ${!isGeoStepValid ? styles.tabDisabled : ''}`}
          onClick={() => goToTab('dashboards')}
        >
          {t('USER.BTN_NEXT_DASHBOARDS')}
          <span className="material-icons" style={{ fontSize: 15 }}>arrow_forward</span>
        </button>
      </div>
    </div>
  );

  const renderDashboardsTab = () => (
    <div>
      <div className={styles.infoNote} style={{ marginBottom: 16 }}>
        <span className="material-icons" style={{ fontSize: 14 }}>info</span>
        {t('USER.DASHBOARDS_INFO')}
      </div>

      {dashboards.length === 0 ? (
        <div className={styles.infoNote}>
          <span className="material-icons" style={{ fontSize: 14 }}>dashboard</span>
          {t('USER.DASHBOARDS_EMPTY')}
        </div>
      ) : (
        dashboards.map((d: any) => (
          <div
            key={d.id}
            style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '12px 16px', marginBottom: 8, borderRadius: 8,
              border: '1px solid var(--border-color)',
              background: 'var(--bg-surface)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span className="material-icons" style={{ fontSize: 20, color: 'var(--primary-1)' }}>dashboard</span>
              <div>
                <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--text-primary)' }}>{d.title}</div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>{d.data?.[0]?.info?.description || d.dashboardId}</div>
              </div>
            </div>
            <span style={{
              fontSize: 11, fontWeight: 600, padding: '2px 10px', borderRadius: 12,
              background: d.isActive ? 'var(--bg-light-2)' : 'var(--bg-surface-secondary)',
              color: d.isActive ? 'var(--state-5)' : 'var(--text-secondary)',
            }}>
              {d.isActive ? 'Active' : 'Inactive'}
            </span>
          </div>
        ))
      )}

      <div className={styles.footer}>
        <button type="button" className={styles.btnCancel} onClick={() => setActiveTab(scopeCategory === 'admin' ? 'basic' : 'geo')}>
          <span className="material-icons" style={{ fontSize: 15 }}>arrow_back</span>
          {t('ACTION.BACK')}
        </button>
        <Button type={ButtonType.secondary} disabled={isSubmitting} onClick={handleSubmit(onSubmit, onInvalid)}>
          {isSubmitting && <span className="spinner-border spinner-border-sm me-2" />}
          <span className="material-icons me-1" style={{ verticalAlign: 'sub' }}>done</span>
          {t('USER.BTN_SAVE_USER')}
        </Button>
      </div>
    </div>
  );

  return (
    <div className={styles.detailPage}>
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <button className={styles.backBtn} onClick={handleBack} title="Back to users">
            <span className="material-icons">arrow_back</span>
          </button>
          <div className={styles.avatarWrap}>
            <Avatar initials={initialGenerator(getValues('firstName') || 'N')} variant="large" />
          </div>
          <div>
            <div className={styles.userName}>{getValues('firstName') || 'New user'}</div>
            <div className={styles.userMeta}>
              {selectedScopeKey ? (
                <span className={styles.roleBadge}>
                  {[...ADMIN_SCOPES, ...DASHBOARD_SCOPES].find((s) => s.key === selectedScopeKey)?.label}
                </span>
              ) : t('USER.SCOPE_ERROR_SELECT')}
            </div>
          </div>
        </div>

        {/* Stepper */}
        <div className={styles.steps}>
          {(['basic', 'geo', 'dashboards'] as const).map((t, i) => (
            <div key={t} className={styles.stepGroup}>
              <div
                className={`${styles.stepDot} ${i < tabIdx ? styles.stepDone : ''} ${i === tabIdx ? styles.stepActive : ''} ${!canAccessTab(t) ? styles.tabDisabled : ''}`}
                onClick={() => goToTab(t)}
              >
                {i < tabIdx ? <span className="material-icons" style={{ fontSize: 11 }}>check</span> : i + 1}
              </div>
              {i < 2 && <div className={styles.stepLine} />}
            </div>
          ))}
        </div>
      </div>

      {/* ── TABS ─────────────────────────────────────────────────────────────── */}
      <div className={styles.tabs}>
        <div className={`${styles.tab} ${activeTab === 'basic' ? styles.tabActive : ''}`} onClick={() => goToTab('basic')}>
          <span className="material-icons" style={{ fontSize: 14 }}>person</span>
          {t('USER.TAB_BASIC')}
        </div>
        {scopeCategory !== 'admin' && (
          <div className={`${styles.tab} ${activeTab === 'geo' ? styles.tabActive : ''} ${!canAccessGeoTab ? styles.tabDisabled : ''}`} onClick={() => goToTab('geo')}>
            <span className="material-icons" style={{ fontSize: 14 }}>place</span>
            {t('USER.TAB_GEO')}
          </div>
        )}
        {selectedScopeKey && (
          <div className={`${styles.tab} ${activeTab === 'dashboards' ? styles.tabActive : ''} ${!canAccessDashboardsTab ? styles.tabDisabled : ''}`} onClick={() => goToTab('dashboards')}>
            <span className="material-icons" style={{ fontSize: 14 }}>dashboard</span>
            {t('USER.TAB_DASHBOARDS')}
          </div>
        )}
      </div>

      <div className={styles.body}>
        <form onSubmit={handleSubmit(onSubmit, onInvalid)}>

          {/* ── BASIC INFO TAB ─────────────────────────────────────────────── */}
          {activeTab === 'basic' && renderBasicTab()}

          {/* ── GEO TAB ────────────────────────────────────────────────────── */}
          {activeTab === 'geo' && renderGeoTab()}

          {/* ── DASHBOARDS TAB ─────────────────────────────────────────────── */}
          {activeTab === 'dashboards' && renderDashboardsTab()}

        </form>
      </div>
    </div>
  );
};